const DATE_DIGITS_LIMIT = 8
const DATE_SEPARATOR = '.'

export function parseMaskedDate(value: string): Date | null {
  const match = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/)

  if (!match) return null

  const [, dayValue, monthValue, yearValue] = match
  const day = Number(dayValue)
  const month = Number(monthValue)
  const year = Number(yearValue)
  const date = new Date(year, month - 1, day)

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return date
}

export function isPastDate(value: string): boolean {
  const date = parseMaskedDate(value)

  if (!date) return false

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  return date.getTime() < today.getTime()
}

export function isFutureOrTodayDate(value: string): boolean {
  const date = parseMaskedDate(value)

  if (!date) return false

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  return date.getTime() >= today.getTime()
}

export class DateInputMask {
  private readonly input: HTMLInputElement

  constructor(input: HTMLInputElement) {
    this.input = input
  }

  public init(): void {
    this.input.inputMode = 'numeric'
    this.input.maxLength = 10

    this.input.addEventListener('keydown', (event) => this.onKeyDown(event))
    this.input.addEventListener('input', () => this.onInput())

    this.formatCurrentValue()
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.metaKey || event.ctrlKey || event.altKey) return

    if (event.key === 'Backspace' && this.shouldDeletePreviousDigit()) {
      event.preventDefault()
      this.deleteDigitBeforeCursor()
      return
    }

    if (event.key.length !== 1) return

    if (!/\d/.test(event.key)) {
      event.preventDefault()
    }
  }

  private onInput(): void {
    const cursorPosition = this.input.selectionStart ?? this.input.value.length
    const digitCursorPosition = this.countDigitsBeforeCursor(
      this.input.value,
      cursorPosition
    )
    const formattedValue = this.formatValue(this.input.value)
    const nextCursorPosition = this.getCursorPositionForDigitIndex(
      formattedValue,
      digitCursorPosition
    )

    this.input.value = formattedValue
    this.setCursorPosition(nextCursorPosition)
  }

  private formatCurrentValue(): void {
    this.input.value = this.formatValue(this.input.value)
  }

  private formatValue(value: string): string {
    const digits = this.getDateDigits(value)

    return this.formatDigits(digits)
  }

  private formatDigits(digits: string): string {
    const day = digits.slice(0, 2)
    const month = digits.slice(2, 4)
    const year = digits.slice(4, 8)

    if (digits.length <= 2) {
      return `${day}${digits.length === 2 ? DATE_SEPARATOR : ''}`
    }

    if (digits.length <= 4) {
      return `${day}${DATE_SEPARATOR}${month}${
        digits.length === 4 ? DATE_SEPARATOR : ''
      }`
    }

    return `${day}${DATE_SEPARATOR}${month}${DATE_SEPARATOR}${year}`
  }

  private getDateDigits(value: string): string {
    const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)

    if (isoDate) {
      const [, year, month, day] = isoDate

      return `${day}${month}${year}`
    }

    return value.replace(/\D/g, '').slice(0, DATE_DIGITS_LIMIT)
  }

  private countDigitsBeforeCursor(
    value: string,
    cursorPosition: number
  ): number {
    return value.slice(0, cursorPosition).replace(/\D/g, '').length
  }

  private shouldDeletePreviousDigit(): boolean {
    const cursorPosition = this.input.selectionStart
    const selectionEnd = this.input.selectionEnd

    return (
      cursorPosition !== null &&
      selectionEnd !== null &&
      cursorPosition === selectionEnd &&
      cursorPosition > 0 &&
      this.input.value[cursorPosition - 1] === DATE_SEPARATOR
    )
  }

  private deleteDigitBeforeCursor(): void {
    const cursorPosition = this.input.selectionStart || this.input.value.length
    const digitCursorPosition = this.countDigitsBeforeCursor(
      this.input.value,
      cursorPosition
    )
    const digits = this.getDateDigits(this.input.value)
    const digitIndexToDelete = Math.max(0, digitCursorPosition - 1)
    const nextDigits = `${digits.slice(0, digitIndexToDelete)}${digits.slice(
      digitIndexToDelete + 1
    )}`
    const formattedValue = this.formatDigits(nextDigits)
    const nextCursorPosition = this.getCursorPositionForDigitIndex(
      formattedValue,
      digitIndexToDelete
    )

    this.input.value = formattedValue
    this.setCursorPosition(nextCursorPosition)
  }

  private getCursorPositionForDigitIndex(
    value: string,
    digitIndex: number
  ): number {
    if (digitIndex <= 0) return 0

    let currentDigitIndex = 0

    for (let index = 0; index < value.length; index += 1) {
      if (/\d/.test(value[index])) {
        currentDigitIndex += 1
      }

      if (currentDigitIndex === digitIndex) {
        let nextPosition = index + 1

        while (value[nextPosition] === DATE_SEPARATOR) {
          nextPosition += 1
        }

        return nextPosition
      }
    }

    return value.length
  }

  private setCursorPosition(position: number): void {
    try {
      this.input.setSelectionRange(position, position)
    } catch {
      // Some input types do not expose text selection APIs.
    }
  }
}
